/*----------------------------------
- HOOK: HOME HERO PICTURE
----------------------------------*/

/**
 * The hero picture the floating dock shows (Home v3, Gaetan, 2026-11-04): Day station, the server's default, or Night
 * launch. While Night launch is shown, `body.hero-night` tells the frame (the phone header's logo turns white over the
 * night picture, `components/layout`); the class leaves when Day station is chosen and when Home unmounts.
 *
 * Decision: the choice is the visit's only, kept in component state. The mock keeps it in the address (`?hero=night`),
 * but Home's HTML is cached and its canonical is `/`: a query parameter would add a second cached variant of the page
 * (or a server render that disagrees with the address), for a picture switch that changes no data. Not stored either:
 * a reload shows Day station, the picture every visitor gets first.
 *
 * @docs docs/features/website-landing-experience
 * @rule The dock changes the picture and its sand colour only, never the data the page shows; SSR always renders Day.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { useEffect, useState } from 'preact/hooks';

import type { HomeHero } from './homeScenes';

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** The class the frame reads on `body` (the chrome's phone header, `.hero-night` in marketing.css). */
export const HERO_NIGHT_BODY_CLASS = 'hero-night';

/** What every visitor gets first, and what the server renders. */
export const DEFAULT_HOME_HERO: HomeHero = 'day';

/*----------------------------------
- HELPERS
----------------------------------*/

/** Sets or clears the night class for `hero`; takes the class list alone so a test can pass its own. */
export const applyHeroBodyClass = (classList: Pick<DOMTokenList, 'toggle'>, hero: HomeHero): void => {
    classList.toggle(HERO_NIGHT_BODY_CLASS, hero === 'night');
};

/*----------------------------------
- HOOKS
----------------------------------*/

export const useHomeHero = (): [HomeHero, (hero: HomeHero) => void] => {
    const [hero, setHero] = useState<HomeHero>(DEFAULT_HOME_HERO);

    useEffect(() => applyHeroBodyClass(document.body.classList, hero), [hero]);
    // Leaving Home (a client navigation) must not leave the next page's header in its night colours.
    useEffect(() => () => applyHeroBodyClass(document.body.classList, DEFAULT_HOME_HERO), []);

    return [hero, setHero];
};

/*----------------------------------
- EXPORTS
----------------------------------*/

export default useHomeHero;
